import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone } from 'lucide-react';
import Navigation from './Navigation';
import SEO from './SEO';
import Testimonials from './Testimonials';
import ContactForm from './ContactForm';
import Footer from './Footer';
import { serviceSchema, faqSchema, breadcrumbSchema } from '../utils/schemas';
const LoanPageTemplate = ({
  icon: Icon,
  title,
  subtitle,
  description,
  features,
  process,
  faqs,
  loanType,
  path,
  guides,
  color = 'from-[#00594C] to-[#003d35]',
}) => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={title}
        description={subtitle}
        path={path}
        schema={[
          serviceSchema(title, subtitle, path),
          breadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: title, url: path },
          ]),
          ...(faqs
            ? [faqSchema(faqs.map((f) => ({ question: f.q, answer: f.a })))]
            : []),
        ]}
      />
      <Navigation />

      {/* Hero */}
      <section className={`relative pt-32 pb-20 bg-gradient-to-br ${color}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-md rounded-2xl mb-6 border border-white/20">
              <Icon className="w-8 h-8 text-[#99DF63]" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">{title}</h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8">{subtitle}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#book-appointment"
                className="inline-flex items-center justify-center space-x-2 bg-[#99DF63] text-[#00594C] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#8BCF52] transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <span>Get Started</span>
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="tel:+61468007056"
                className="inline-flex items-center justify-center space-x-2 bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white/20 transition-all border border-white/30"
              >
                <Phone className="w-5 h-5" />
                <span>0468 007 056</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed">{description}</p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-[#00594C] text-center mb-12">
            Why Choose Us
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {features.map((feature, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#99DF63]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-[#00594C]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#00594C] mb-1">{feature.title}</h3>
                    <p className="text-gray-600 text-sm">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* General Advice Warning */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white border border-amber-200 rounded-xl p-6">
            <p className="text-sm text-gray-600">
              <strong className="text-gray-800">General Advice Warning:</strong> The information provided on this page is general in nature and does not take into account your objectives, financial situation or needs. You should consider whether it is appropriate for you before acting on it.
            </p>
          </div>
        </div>
      </section>

      {/* Process */}
      {process && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-[#00594C] text-center mb-12">
              How It Works
            </h2>
            <div className="max-w-3xl mx-auto space-y-8">
              {process.map((step, i) => (
                <div key={i} className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-[#00594C] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#00594C] text-lg mb-1">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      {faqs && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-[#00594C] text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, i) => (
                <details key={i} className="bg-white rounded-xl border border-gray-100 group">
                  <summary className="px-6 py-4 cursor-pointer font-semibold text-[#00594C] hover:text-[#99DF63] transition-colors list-none flex items-center justify-between">
                    {faq.q}
                    <svg className="w-5 h-5 transition-transform group-open:rotate-180 flex-shrink-0 ml-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="px-6 pb-4 text-gray-600">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {guides && guides.length > 0 && (
        <section className="py-16 bg-white" aria-labelledby="related-guides">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 id="related-guides" className="text-2xl md:text-3xl font-bold text-[#00594C] mb-6">
              Related guides
            </h2>
            <ul className="grid md:grid-cols-2 gap-4">
              {guides.map((g) => (
                <li key={g.href} className="bg-gray-50 rounded-xl border border-gray-100 p-5 hover:border-[#99DF63] transition-colors">
                  <Link to={g.href} className="font-semibold text-[#00594C] hover:text-[#99DF63] transition-colors">
                    {g.title}
                  </Link>
                  <p className="text-sm text-gray-600 mt-1">{g.blurb}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <Testimonials />
      <ContactForm pageContext={loanType} />
      <Footer />
    </div>
  );
};

export default LoanPageTemplate;
